"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Serialization = exports.JsonErrorPrefix = void 0;
const echain_shared_validation_1 = require("@igus/echain-shared-validation");
/**
 * The error message prefix used at validation if any json property is not known.
 *
 * @deprecated Don't use oo modelling anymore. Use simple types or `shared-modelling` instead.
 */
var JsonErrorPrefix;
(function (JsonErrorPrefix) {
    JsonErrorPrefix["unknownProperty"] = "unknown_property__";
})(JsonErrorPrefix = exports.JsonErrorPrefix || (exports.JsonErrorPrefix = {}));
/**
 * The static `Serialization` class provides methods to serialize, deserialize and validate json
 * objects representing classes.
 *
 * @deprecated Don't use oo modelling anymore. Use simple types or `shared-modelling` instead.
 */
class Serialization {
    /**
     * Merges multiple `JsonMapping` objects into one. It is useful to support json representations of
     * noMerge classes.
     * The merges is applied from right to left resulting in a new object. The original mappings are
     * not changed.
     *
     * @param jsonMappings The `JsonMapping` objects to merge.
     * @returns The merged `JsonMapping`.
     */
    static mergeJsonMappings(...jsonMappings) {
        const mergedMap = {};
        jsonMappings.forEach((map) => {
            echain_shared_validation_1.Validation.throws(echain_shared_validation_1.Validator.definedNotNull(map, 'Serialization.mergeJsonMappings: invalid jsonMapping'));
            for (const key in map) {
                const value = map[key];
                // do not merge an noMerge property which would overwrite the real mapping.
                if (typeof value === 'object' && value.noMerge) {
                    continue;
                }
                mergedMap[key] = value;
            }
        });
        return mergedMap;
    }
    /**
     * Maps a json of `J` into a class of `C` using the mapping definition provided as `JsonMapping`
     *
     * @param json The json to map from.
     * @param instance The class instance to map into.
     * @param jsonMapping The mapping definition.
     * @returns A validation object with collected issues occured during mapping.
     */
    static fromJson(json, instance, jsonMapping) {
        echain_shared_validation_1.Validation.throws(echain_shared_validation_1.Validator.definedNotNull(json, 'Serialization.fromJson: invalid json for class ' + Serialization.getClassName(instance)));
        const validation = echain_shared_validation_1.Validation.empty();
        let jsonKey;
        // for all properties in the json
        for (jsonKey in json) {
            const jsonValue = json[jsonKey];
            // skip null & undefined
            if (jsonValue == null) {
                continue;
            }
            // get mapping of current loop iteration
            let mappingConfig = jsonMapping[jsonKey];
            // simple string-based mapping from json to class
            if (typeof mappingConfig === 'string') {
                mappingConfig = mappingConfig;
                instance[mappingConfig] = jsonValue;
            }
            // complex mapping defined by config object
            else if (mappingConfig && typeof mappingConfig === 'object') {
                mappingConfig = mappingConfig;
                // skip this json property if marked as `noFromJson`
                if (mappingConfig.noFromJson)
                    continue;
                // property to set is either the public class 'prop' or the private|protected class '_prop'
                const prop = mappingConfig._prop || mappingConfig.prop;
                // verify that we have a prop
                if (!prop) {
                    throw new Error('Serialization.fromJson: No prop or _prop defined for class ' + Serialization.getClassName(instance));
                }
                // mapping via fromJson method
                if (mappingConfig.fromJson) {
                    instance[prop] = mappingConfig.fromJson(jsonValue, json);
                }
                // default mapping using prop or _prop
                else {
                    instance[prop] = jsonValue;
                }
            }
            else if (jsonKey === 'schemaVersion') {
                // the schemaVersion is a well-known meta information, we just do nothing
            }
            // populate validation if we do not have a mapping definition of that json property
            else {
                validation.add(JsonErrorPrefix.unknownProperty + jsonKey.toString());
            }
        }
        return validation;
    }
    /**
     * Maps an instance property of `C` to a json property of `J`.
     *
     * Since properties of typescript classes are not declared as properties of the ES5 prototype,
     * we have no chance to check whether the definiton of `_prop` really exists.
     * So we cannot populate a `Validation` object as the `fromJson` method does.
     *
     * @param instance The instance to map from.
     * @param jsonMapping The `JsonMapping` defining how to map.
     * @returns The mapped json.
     */
    static toJson(instance, jsonMapping) {
        echain_shared_validation_1.Validation.throws([
            echain_shared_validation_1.Validator.definedNotNull(instance, 'Serialization.toJson: invalid instance of class'),
            echain_shared_validation_1.Validator.definedNotNull(jsonMapping, 'Serialization.toJson: invalid jsonMapping of class ' + Serialization.getClassName(instance)),
        ]);
        // populate all keys by merging all instance property names with those of its __proto__
        const instanceKeys = Object.getOwnPropertyNames(instance);
        if ('__proto__' in instance) {
            const instanceKeysOfProto = Object.getOwnPropertyNames(instance.__proto__);
            const instanceKeysOfProtoWithoutConstructor = instanceKeysOfProto.filter((key) => key !== 'constructor');
            instanceKeys.push(...instanceKeysOfProtoWithoutConstructor);
        }
        const json = {};
        let instanceKey;
        let jsonMappingKey;
        for (instanceKey of instanceKeys) {
            for (jsonMappingKey in jsonMapping) {
                let jsonMappingTarget = jsonMapping[jsonMappingKey];
                // assign to json by simple string mapping
                if (typeof jsonMappingTarget === 'string' && jsonMappingTarget === instanceKey) {
                    Serialization.assignJson(json, jsonMappingKey, Serialization.getJsonValue(instance, instanceKey));
                    break;
                }
                // assign to json using a mapping config
                else if (typeof jsonMappingTarget === 'object') {
                    jsonMappingTarget = jsonMappingTarget;
                    if (jsonMappingTarget.prop === instanceKey || jsonMappingTarget._prop === instanceKey) {
                        this.assignMappingConfigToJson(instance, jsonMappingTarget, json, jsonMappingKey);
                        break;
                    }
                }
            }
        }
        // check virtual properties, they are not present IN instance..
        const virtualPropertyJsonMapping = Object.keys(jsonMapping).reduce((mapping, curKey) => {
            const virtualJsonMapping = jsonMapping[curKey];
            return !!virtualJsonMapping && virtualJsonMapping.isVirtual
                ? Object.assign(mapping, { [curKey]: virtualJsonMapping })
                : mapping;
        }, {});
        for (jsonMappingKey in virtualPropertyJsonMapping) {
            const jsonMappingTarget = virtualPropertyJsonMapping[jsonMappingKey];
            this.assignMappingConfigToJson(instance, jsonMappingTarget, json, jsonMappingKey);
        }
        return json;
    }
    /**
     * Assigns the instance value to the json using the proper method or property.
     *
     * If a `toJson` method is defined, it is used,
     * otherwise the instance properties defined as `_prop` or `prop` are used.
     */
    static assignMappingConfigToJson(instance, jsonMappingTarget, json, jsonMappingKey) {
        if (jsonMappingTarget.toJson) {
            Serialization.assignJson(json, jsonMappingKey, jsonMappingTarget.toJson.call(instance, instance));
        }
        else {
            const instanceProp = jsonMappingTarget._prop || jsonMappingTarget.prop;
            Serialization.assignJson(json, jsonMappingKey, Serialization.getJsonValue(instance, instanceProp));
        }
    }
    /**
     * Validates a given json by performing simple checks whether a json property is known or not.
     *
     * @param json The json to validate.
     * @param jsonMapping The definiton used for validation and nested json validation.
     * @returns The validation result.
     */
    static validateJson(json, jsonMapping) {
        echain_shared_validation_1.Validation.throws([
            echain_shared_validation_1.Validator.definedNotNull(json, 'Serialization.validateJson: invalid json'),
            echain_shared_validation_1.Validator.definedNotNull(jsonMapping, 'Serialization.validateJson: invalid jsonMapping'),
        ]);
        const validation = echain_shared_validation_1.Validation.empty();
        for (const key in json) {
            const jsonValue = json[key];
            // property is known
            if (key in jsonMapping) {
                // we have a JsonMappingConfig for the property
                if (typeof jsonMapping[key] === 'object') {
                    const jsonMappingValue = jsonMapping[key];
                    // property is a child json with its own mapping -> go into recursion
                    if (jsonMappingValue.child || jsonMappingValue.childResolver) {
                        Serialization.validateChildJson(jsonValue, jsonMappingValue, validation);
                    }
                    // property is an array of child jsons with its own mapping -> check recursive
                    else if (jsonMappingValue.children || jsonMappingValue.childrenResolver) {
                        Serialization.validateChildrenJson(jsonValue, jsonMappingValue, validation);
                    }
                }
            }
            else if (key === 'schemaVersion') {
                // the schemaVersion is a well-known meta information, we just do nothing
            }
            // property is unknown (in mapping)
            else {
                validation.add(JsonErrorPrefix.unknownProperty + key);
            }
        }
        return validation;
    }
    /**
     * Validates a single child json.
     *
     * @param childJson The json to validate.
     * @param jsonMappingConfig The mapping definition of the child json.
     * @param validation The validation object to populate.
     */
    static validateChildJson(childJson, jsonMappingConfig, validation) {
        if (childJson != null) {
            // a child `JsonMapping`
            if (jsonMappingConfig.child) {
                validation.add(Serialization.validateJson(childJson, jsonMappingConfig.child));
            }
            // a function resolving the `JsonMapping` of the child
            else if (jsonMappingConfig.childResolver) {
                validation.add(Serialization.validateJson(childJson, jsonMappingConfig.childResolver(childJson)));
            }
            // throw if definition is invalid
            else {
                throw new Error('Serialization.validateChildJson: jsonMappingConfig has neither child nor childResolver declared');
            }
        }
    }
    /**
     * Validate multiple child jsons.
     *
     * @param childJsons The array of child jsons to validate.
     * @param jsonMappingConfig The mapping definition of the child array.
     * @param validation The `Validation` object to populate.
     */
    static validateChildrenJson(childJsons, jsonMappingConfig, validation) {
        if (childJsons != null) {
            if (!Array.isArray(childJsons)) {
                throw new Error('Serialization.validateChildrenJson: Property is not an Array: ' + childJsons);
            }
            childJsons.forEach((childJson) => {
                // a child `JsonMapping`
                if (jsonMappingConfig.children) {
                    const jsonChildMapping = jsonMappingConfig.children;
                    validation.add(Serialization.validateJson(childJson, jsonChildMapping));
                }
                // a function resolving the `JsonMapping` of each child in the array
                else if (jsonMappingConfig.childrenResolver) {
                    const jsonChildMapping = jsonMappingConfig.childrenResolver(childJson);
                    validation.add(Serialization.validateJson(childJson, jsonChildMapping));
                }
                // throw if definition is invalid
                else {
                    throw new Error('Serialization.validateChildrenJson: jsonMappingConfig has neither children nor childrenResolver declared');
                }
            });
        }
    }
    /**
     * Retrieves the json value of a class `C` instance property.
     * It automatically uses a `toJson` method if available.
     *
     * @param instance The instance to get the value from.
     * @param instanceKey The property key to get the value from.
     * @returns The value.
     */
    static getJsonValue(instance, instanceKey) {
        const instanceValue = instance[instanceKey];
        if (instanceValue) {
            return typeof instanceValue.toJson == 'function' ? instanceValue.toJson() : instanceValue;
        }
        return instanceValue;
    }
    /**
     * Assings a value to a json of `J`
     *
     * @param json The json to assign to.
     * @param key The key of the json to assign to.
     * @param value The value to assign.
     */
    static assignJson(json, key, value) {
        if (value != null) {
            json[key] = value;
        }
    }
    /**
     * Retrieves the class name of an instance. It is used for Error objects.
     *
     * @param instance The instance to get the name from.
     * @returns The name.
     */
    static getClassName(instance) {
        return instance && instance.constructor ? instance.constructor.name : undefined;
    }
    /**
     * Retrieves an array of serializable class properties. It will contain only those which have at least a config using string literal, prop or _prop.
     */
    static getClassProperties(jsonMapping) {
        const classProps = [];
        // map json to instance props
        for (const jsonProp in jsonMapping) {
            const classPropDef = jsonMapping[jsonProp];
            let classProp;
            let classPropDefAsString;
            // string => public instance prop
            if (typeof classPropDef === 'string') {
                classProp = classPropDef;
                classPropDefAsString = classPropDef;
            }
            // object => private "_prop" || public "prop"
            else if (typeof classPropDef === 'object' && classPropDef) {
                if (classPropDef.noFromJson) {
                    continue;
                }
                classProp = (classPropDef._prop || classPropDef.prop);
                classPropDefAsString = JSON.stringify(classPropDef);
            }
            // unknown
            else {
                throw new Error(`${this.className}.getClassProperties: unknown json mapping config: ${classPropDef.toString()}`);
            }
            // populate staticInstanceProperties
            if (classProp) {
                classProps.push(classProp);
            }
            else {
                throw new Error(`${this.className}.getClassProperties: falsy instance prop should not exist using: ${classPropDefAsString}`);
            }
        }
        return classProps;
    }
}
exports.Serialization = Serialization;
Serialization.className = 'Serialization';
//# sourceMappingURL=Serialization.js.map